/**
 * Suppresses noisy Amazon Connect Streams console output.
 *
 * Call `installStreamsLogFilter()` once at app bootstrap (before CCP init).
 * The filter is NOT applied automatically on module import — this prevents
 * jsdom test suites from losing their console output.
 *
 * Debug mode: run `window.__acUnfilter()` in the browser console to see ALL
 * Connect Streams logs (useful for debugging putAgentState errors, etc.).
 * Run `window.__acRefilter()` to re-enable filtering.
 */

let installed = false;
let filtered = false; // DEBUG: show all Connect Streams logs

export function installStreamsLogFilter(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  const originalConsoleLog = console.log;
  const originalConsoleInfo = console.info;
  const originalConsoleWarn = console.warn;

  const shouldFilterLog = (args: unknown[]): boolean => {
    if (!filtered) return false; // debug mode — show everything

    const logString = args
      .map(arg => {
        if (typeof arg === 'string') return arg;
        if (arg && typeof arg === 'object') {
          try {
            return JSON.stringify(arg);
          } catch {
            return String(arg);
          }
        }
        return String(arg);
      })
      .join(' ');

    const isStreamsLog =
      logString.includes('[INFO]') || logString.includes('[LOG]');

    if (
      logString.includes('BackgroundBlurProcessor') ||
      logString.includes('model successfully loaded') ||
      logString.includes('ConsoleLogger')
    ) {
      return true;
    }

    if (isStreamsLog) {
      return true;
    }

    const hasAgentId =
      /\[[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}\]/.test(
        logString
      );

    return (
      hasAgentId ||
      logString.includes('RtcPeerConnectionManager') ||
      logString.includes('RtcPeerConnectionFactory') ||
      logString.includes('getDomainId') ||
      logString.includes('voiceId') ||
      logString.includes('Quick Responses') ||
      logString.includes('Softphone Manager') ||
      logString.includes('SoftphoneManager') ||
      logString.includes('AMZ_WEB_SOCKET_MANAGER') ||
      logString.includes('Agent initialized') ||
      logString.includes('RTC.js is using') ||
      logString.includes('StandardStrategy') ||
      logString.includes('amazon-connect-streams')
    );
  };

  console.log = (...args: unknown[]) => {
    if (!shouldFilterLog(args)) {
      originalConsoleLog.apply(console, args);
    }
  };

  console.info = (...args: unknown[]) => {
    if (!shouldFilterLog(args)) {
      originalConsoleInfo.apply(console, args);
    }
  };

  console.warn = originalConsoleWarn;

  // Debug toggles
  const win = window as unknown as Record<string, unknown>;
  win.__acUnfilter = () => {
    filtered = false;
    console.log('[AC-Filter] UNFILTERED — showing ALL Connect Streams logs');
  };
  win.__acRefilter = () => {
    filtered = true;
    console.log('[AC-Filter] FILTERED — suppressing Connect Streams noise');
  };
}
